import { ref, computed, watch } from 'vue'
import { useApplicationStore } from '../../../model/store'

const INFO_KINDS = ['customer', 'applicant']

const emptyInfo = (kind) => ({
  id: null,
  kind,
  surname: '',
  name: '',
  patronymic: '',
  birthdate: '',
  phone: '',
  email: '',
  birth_place_address: '',
  registration_address: '',
  identity_document_type: '',
  identity_series: '',
  identity_number: '',
  identity_date: '',
  identity_contragent: '',
  identity_contragent_code: '',
})

const blankForm = () => ({
  contest_group_id: null,
  profile_id: null,
  contract_type_id: null,
  customer_kind: null,
})

const blankInfos = () => Object.fromEntries(INFO_KINDS.map(kind => [kind, emptyInfo(kind)]))

export function useContractForm(contract) {
  const store = useApplicationStore()

  const form = ref(blankForm())
  const infos = ref(blankInfos())
  const paymentTypeIdByKind = ref({})

  // customer-блок виден всегда (когда выбран тип заказчика);
  // applicant-блок — только когда заказчик — иное лицо (other_person).
  const visibleInfoKinds = computed(() => {
    if (!form.value.customer_kind) return []
    return INFO_KINDS.filter(kind => kind === 'customer' || form.value.customer_kind !== kind)
  })

  const findSavedInfo = (kind) =>
    contract.value?.contract_infos?.find(info => info.kind === kind)

  const findSavedPaymentType = (paymentKind) =>
    contract.value?.contract_payment_types?.find(item => item.kind === paymentKind)

  const populate = () => {
    const saved = contract.value
    if (!saved) {
      form.value = blankForm()
      infos.value = blankInfos()
      paymentTypeIdByKind.value = {}
      return
    }

    form.value = {
      contest_group_id: saved.contest_group_id,
      profile_id: saved.profile_id,
      contract_type_id: saved.contract_type_id,
      customer_kind: saved.customer_kind,
    }

    infos.value = Object.fromEntries(INFO_KINDS.map(kind => {
      const savedInfo = findSavedInfo(kind)
      return [kind, savedInfo ? { ...emptyInfo(kind), ...savedInfo } : emptyInfo(kind)]
    }))

    paymentTypeIdByKind.value = Object.fromEntries(
      (saved.contract_payment_types ?? []).map(item => [item.kind, item.payment_type_id])
    )
  }

  watch(contract, populate, { immediate: true })

  const moveInfo = (from, to) => {
    infos.value[to] = { ...infos.value[from], kind: to }
    infos.value[from] = emptyInfo(from)
  }

  const setCustomerKind = (customerKind) => {
    const previousCustomerKind = form.value.customer_kind
    form.value.customer_kind = customerKind

    if (previousCustomerKind === 'applicant' && customerKind === 'other_person') moveInfo('customer', 'applicant')
    if (previousCustomerKind === 'other_person' && customerKind === 'applicant') moveInfo('applicant', 'customer')
  }

  // Скрытые info-блоки помечаются _destroy, чтобы Rails удалил привязанный contract_info,
  // если он раньше был сохранён.
  const buildInfoAttribute = (kind) => {
    const saved = findSavedInfo(kind)
    if (!visibleInfoKinds.value.includes(kind)) {
      return saved ? { id: saved.id, vue_index: kind, _destroy: true } : null
    }
    return { ...infos.value[kind], id: saved?.id ?? null, vue_index: kind }
  }

  const buildPaymentTypeAttribute = (paymentKind) => {
    const saved = findSavedPaymentType(paymentKind.value)
    return {
      id: saved?.id ?? null,
      kind: paymentKind.value,
      payment_type_id: paymentTypeIdByKind.value[paymentKind.value] ?? null,
      vue_index: paymentKind.value,
    }
  }

  const buildPayload = () => ({
    ...form.value,
    contract_infos_attributes: INFO_KINDS.map(buildInfoAttribute).filter(Boolean),
    contract_payment_types_attributes: store.contractPaymentTypeKinds.map(buildPaymentTypeAttribute),
  })

  return { form, infos, paymentTypeIdByKind, visibleInfoKinds, setCustomerKind, buildPayload }
}
